import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Kanban, List, Plus, Trash2, TrendingUp, Lightbulb, ArrowUpCircle, Bot } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { projectApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';
import { useWorkItemStore } from '../store/useWorkItemStore';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';
import { useOrgHierarchyStore } from '../store/useOrgHierarchyStore';
import RefreshButton from '../components/common/RefreshButton';
import KanbanBoard from '../components/kanban/KanbanBoard';
import TaskListView from '../components/tasks/TaskListView';
import CreateWorkItemModal from '../components/workitems/CreateWorkItemModal';
import RunnerSettingsModal from '../components/projects/RunnerSettingsModal';
import type { Project } from '../types';

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { projects, deleteProject, updateProject } = useProjectStore();
  const { items, fetchByProject } = useWorkItemStore();
  const { viewMode, setViewMode, setSelectedProject } = useAppStore();
  const { getHealthColor } = useConfigStore();
  const { organizations, programs, releaseTrains, fetchOrganizations, fetchPrograms, fetchReleaseTrains } = useOrgHierarchyStore();
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [showRunner, setShowRunner] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoteName, setPromoteName] = useState('');
  const [promoteBusy, setPromoteBusy] = useState(false);

  useEffect(() => {
    fetchOrganizations();
    fetchPrograms();
    fetchReleaseTrains();
  }, []);

  // Filtered programs/release trains based on project's current selections
  const filteredPrograms = useMemo(() => {
    if (!project?.organizationId) return programs;
    return programs.filter((p) => p.organizationId === project.organizationId);
  }, [programs, project?.organizationId]);

  const filteredReleaseTrains = useMemo(() => {
    if (!project?.programId) return releaseTrains;
    return releaseTrains.filter((rt) => rt.programId === project.programId);
  }, [releaseTrains, project?.programId]);

  // SP and effort rollup stats from epic-level items (which have rolled-up values)
  const projectStats = useMemo(() => {
    const epics = items.filter((i) => i.workItemType === 'epic' && i.projectId === projectId);
    const totalSP = epics.reduce((s, i) => s + (i.storyPoints || 0), 0);
    const totalEst = epics.reduce((s, i) => s + (i.estimatedHours || 0), 0);
    const totalLogged = epics.reduce((s, i) => s + (i.loggedHours || 0), 0);
    const scored = items.filter((i) =>
      ['epic', 'feature', 'story'].includes(i.workItemType) &&
      i.projectId === projectId &&
      (i.wsjfScore || 0) > 0
    ).length;
    return { totalSP, totalEst, totalLogged, scored, epicCount: epics.length };
  }, [items, projectId]);

  const handleHierarchyUpdate = async (field: string, value: string) => {
    if (!project || !projectId) return;
    try {
      const updates: Record<string, string> = { [field]: value };
      // Cascade clears
      if (field === 'organizationId') {
        updates.programId = '';
        updates.releaseTrainId = '';
      } else if (field === 'programId') {
        updates.releaseTrainId = '';
        // Auto-set org from program
        const prog = programs.find((p) => p._id === value);
        if (prog) updates.organizationId = prog.organizationId;
      } else if (field === 'releaseTrainId') {
        // Auto-set program and org from RT
        const rt = releaseTrains.find((r) => r._id === value);
        if (rt) {
          updates.programId = rt.programId;
          updates.organizationId = rt.organizationId || '';
          const prog = programs.find((p) => p._id === rt.programId);
          if (prog && !updates.organizationId) updates.organizationId = prog.organizationId;
        }
      }
      await updateProject(projectId, updates as any);
      setProject({ ...project, ...updates } as Project);
    } catch {
      toast.error('Failed to update hierarchy');
    }
  };

  useEffect(() => {
    if (!projectId) return;
    setSelectedProject(projectId);
    fetchByProject(projectId);

    // Prefer the store when it already has it, but do not depend on it: nothing
    // guarantees the list has been loaded on the path that got us here, and
    // relying on it is why a direct navigation could render "Project not found".
    const fromStore = projects.find((p) => p._id === projectId);
    if (fromStore) {
      setProject(fromStore);
      return;
    }

    let alive = true;
    projectApi
      .get(projectId)
      .then((p) => { if (alive && p) setProject(p as Project); })
      .catch(() => { if (alive) setProject(null); });
    return () => { alive = false; };
  }, [projectId, projects, setSelectedProject, fetchByProject]);

  const handleDelete = async () => {
    if (!projectId || !confirm('Delete this project and all its work items?')) return;
    try {
      await deleteProject(projectId);
      toast.success('Project deleted');
      navigate('/');
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const openPromote = () => {
    if (!project?.organizationId) {
      toast.error('Assign this project to an organization before promoting it.');
      return;
    }
    setPromoteName(project?.name || '');
    setPromoting(true);
  };

  const handlePromote = async () => {
    if (!projectId || !promoteName.trim()) return;
    setPromoteBusy(true);
    try {
      const { program } = await projectApi.promoteToProgram(projectId, promoteName.trim());
      await Promise.all([fetchPrograms(), fetchProjects()]);
      setProject((p) => (p ? { ...p, programId: program._id } : p));
      toast.success(`Promoted — program “${program.name}” created`);
      setPromoting(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to promote project');
    } finally {
      setPromoteBusy(false);
    }
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
        <p>Project not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Project Header */}
      <header className="shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getHealthColor(project.health) }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {project.displayId && <span className="text-sm font-mono text-gray-400 dark:text-gray-500 mr-2">{project.displayId}</span>}
                {project.name}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {project.status} &middot; {project.priority} priority
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('kanban')}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                  viewMode === 'kanban'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                )}
              >
                <Kanban size={14} />
                Board
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                )}
              >
                <List size={14} />
                List
              </button>
            </div>

            <RefreshButton
              onRefresh={async () => { if (projectId) await fetchByProject(projectId); }}
              tooltip="Refresh work items"
            />

            {/* Ideas for this project */}
            <button
              onClick={() => navigate(`/project/${projectId}/ideation`)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded-lg text-sm hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
              title="Propose & review ideas for this project"
            >
              <Lightbulb size={14} />
              Ideas
            </button>

            {/* Add work item */}
            <button
              onClick={() => setShowCreateItem(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white rounded-lg text-sm hover:bg-accent/90 transition-colors"
            >
              <Plus size={14} />
              Add Item
            </button>

            {/* Promote to program */}
            <button
              onClick={openPromote}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Create a program from this project and move it under it"
            >
              <ArrowUpCircle size={14} />
              Promote to Program
            </button>

            {/* Ways runner sessions for this project (the server checks project:edit) */}
            <button
              onClick={() => setShowRunner(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Ways runner sessions: let Ways start an agent on the server for requests here"
            >
              <Bot size={14} />
              Runner
            </button>

            {/* More actions */}
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete project"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {project.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-2xl">{project.description}</p>
        )}

        {/* The why and the how. Rendered as collapsed panels rather than more
            paragraphs under the title: they are reference material somebody
            opens deliberately, and inlining ~1000 characters each would push
            the board and the work below the fold on every visit. */}
        {(project.problemDetails || project.technicalDescription) && (
          <div className="mt-3 space-y-2 max-w-3xl">
            {project.problemDetails && (
              <details className="group rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                  Problem Details
                  <span className="ml-2 font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                    why this exists
                  </span>
                </summary>
                <p className="px-3 pb-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {project.problemDetails}
                </p>
              </details>
            )}
            {project.technicalDescription && (
              <details className="group rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                  Technical Description
                  <span className="ml-2 font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                    how it is built, and how it fits
                  </span>
                </summary>
                <p className="px-3 pb-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {project.technicalDescription}
                </p>
              </details>
            )}
          </div>
        )}

        {/* SAFe Hierarchy Assignment */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Org:</span>
            <select
              value={project.organizationId || ''}
              onChange={(e) => handleHierarchyUpdate('organizationId', e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">— None —</option>
              {organizations.map((o) => (
                <option key={o._id} value={o._id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Program:</span>
            <select
              value={project.programId || ''}
              onChange={(e) => handleHierarchyUpdate('programId', e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">— None —</option>
              {filteredPrograms.map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">Release Train:</span>
            <select
              value={project.releaseTrainId || ''}
              onChange={(e) => handleHierarchyUpdate('releaseTrainId', e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">— None —</option>
              {filteredReleaseTrains.map((rt) => (
                <option key={rt._id} value={rt._id}>{rt.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* SP & Effort Rollup */}
        {projectStats.epicCount > 0 && (
          <div className="flex items-center gap-5 mt-3 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <TrendingUp size={12} className="text-accent" />
              <span>SP: <strong className="text-gray-800 dark:text-gray-200">{projectStats.totalSP}</strong></span>
            </div>
            <span>Est: <strong className="text-gray-800 dark:text-gray-200">{projectStats.totalEst}h</strong></span>
            <span>Logged: <strong className="text-gray-800 dark:text-gray-200">{projectStats.totalLogged}h</strong></span>
            {projectStats.totalEst > 0 && (
              <span>Variance: <strong className={projectStats.totalEst - projectStats.totalLogged >= 0 ? 'text-green-600' : 'text-red-600'}>
                {(projectStats.totalEst - projectStats.totalLogged).toFixed(0)}h
              </strong></span>
            )}
            {projectStats.scored > 0 && (
              <span>WSJF Scored: <strong className="text-gray-800 dark:text-gray-200">{projectStats.scored}</strong></span>
            )}
          </div>
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'kanban' ? <KanbanBoard /> : <TaskListView />}
      </div>

      {showCreateItem && projectId && (
        <CreateWorkItemModal
          projectId={projectId}
          onClose={() => setShowCreateItem(false)}
        />
      )}

      {showRunner && projectId && (
        <RunnerSettingsModal projectId={projectId} onClose={() => setShowRunner(false)} />
      )}

      {/* Promote to Program */}
      {promoting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !promoteBusy && setPromoting(false)}>
          <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpCircle className="text-accent" size={20} />
              <h2 className="text-base font-semibold">Promote to Program</h2>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Creates a new program in <b>{organizations.find((o) => o._id === project?.organizationId)?.name || 'this organization'}</b> and moves
              <b> {project?.name}</b> under it. The project and its work are unchanged.
            </p>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Program name</label>
            <input
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-accent outline-none"
              value={promoteName}
              onChange={(e) => setPromoteName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePromote(); }}
              placeholder="Program name"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPromoting(false)} disabled={promoteBusy} className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg">Cancel</button>
              <button onClick={handlePromote} disabled={promoteBusy || !promoteName.trim()} className="px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg disabled:opacity-40">
                {promoteBusy ? 'Promoting…' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
